<script setup lang="ts">
import { computed } from 'vue';
import { getLocalizedText } from '../utils/formHelpers';
import type { FormStructure, FormField, Section } from '../types/formTypeStructure';
import { extractAllFields, isOTPField } from '../types/formUtils';
import H4Title from '../../text/H4Title.vue';
import PContent from '../../text/PContent.vue';

interface Props {
  form: FormStructure;
  formValues: Record<string, any>;
  currentLang: string;
}

const props = defineProps<Props>();

// Récupérer tous les champs remplis (non vides et non OTP)
const filledFields = computed(() => {
  const allFields = extractAllFields(props.form);
  return allFields.filter(field => {
    // Masquer les champs OTP
    if (isOTPField(field)) {
      return false;
    }
    
    const value = props.formValues[field.fieldName];
    // Considérer comme rempli si la valeur existe et n'est pas vide
    if (value === undefined || value === null) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });
});

// Formater la valeur pour l'affichage
function formatValue(field: FormField, value: any): string {
  if (value === null || value === undefined) return '-';
  
  // Pour les dropdowns/selects, afficher le label de l'option
  if (field.fieldType === 'dropdown' && field.selectOptions) {
    const option = field.selectOptions.find(opt => {
      const optValue = typeof opt.value === 'string' ? opt.value : String(opt.value);
      return optValue === String(value);
    });
    if (option) {
      return getLocalizedText(option.label, props.currentLang);
    }
  }
  
  // Pour les radio buttons
  if (field.fieldType === 'radio' && field.selectOptions) {
    const option = field.selectOptions.find(opt => {
      const optValue = typeof opt.value === 'string' ? opt.value : String(opt.value);
      return optValue === String(value);
    });
    if (option) {
      return getLocalizedText(option.label, props.currentLang);
    }
  }
  
  // Pour les checkboxes multiples
  if (field.fieldType === 'checkbox' && Array.isArray(value) && field.selectOptions) {
    return value.map(v => {
      const option = field.selectOptions?.find(opt => {
        const optValue = typeof opt.value === 'string' ? opt.value : String(opt.value);
        return optValue === String(v);
      });
      return option ? getLocalizedText(option.label, props.currentLang) : String(v);
    }).join(', ');
  }
  
  // Pour les fichiers
  if (value instanceof File) return value.name;
  if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
    return value.map(f => f.name).join(', ');
  }
  
  // Pour les dates
  if (field.fieldType === 'date' && value) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString(props.currentLang === 'fr' ? 'fr-FR' : 'en-US');
      }
    } catch (e) {
      // Si la conversion échoue, afficher la valeur telle quelle
    }
  }
  
  // Pour les booléens
  if (field.fieldType === 'bool' && typeof value === 'boolean') {
    return value 
      ? getLocalizedText({ fr: 'Oui', en: 'Yes' }, props.currentLang)
      : getLocalizedText({ fr: 'Non', en: 'No' }, props.currentLang);
  }
  
  // Par défaut, convertir en string
  return String(value);
}

// Grouper les champs par section
const fieldsBySection = computed(() => {
  const grouped: Array<{ section: Section; fields: FormField[] }> = [];
  
  props.form.sections.forEach(section => {
    const sectionFields = filledFields.value.filter(field => {
      // Trouver dans quelle section se trouve ce champ
      return section.formFields?.some(f => f.fieldName === field.fieldName);
    });
    
    if (sectionFields.length > 0) {
      grouped.push({
        section,
        fields: sectionFields
      });
    }
  });
  
  return grouped;
});
</script>

<template>
  <div
    class="border-2 border-slate-200 w-full max-w-[1080px] bg-white rounded-[50px] p-2 tablet:p-8 flex flex-col items-center transition-opacity duration-500 opacity-100"
  >
    <H4Title class="text-lg font-medium text-gray-700 mb-4 pb-2 border-b border-gray-200 w-full text-center">
      {{ getLocalizedText({ fr: 'Aperçu du formulaire', en: 'Form preview' }, currentLang) }}
    </H4Title>

    <PContent class="text-gray-600 mb-6 w-full text-center">
      {{ getLocalizedText({ 
        fr: 'Veuillez vérifier les informations saisies avant de soumettre le formulaire.', 
        en: 'Please review the information entered before submitting the form.' 
      }, currentLang) }}
    </PContent>

    <div class="w-full space-y-6">
      <div
        v-for="(group, index) in fieldsBySection"
        :key="group.section.sectionId || index"
        class="border border-gray-200 rounded-lg p-4 bg-gray-50"
      >
        <h3 class="text-md font-semibold text-gray-800 mb-3">
          {{ getLocalizedText(group.section.title || undefined, currentLang) }}
        </h3>
        
        <div class="space-y-3">
          <div
            v-for="field in group.fields"
            :key="field.fieldName"
            class="flex flex-col tablet:flex-row tablet:items-start gap-2 py-2 border-b border-gray-100 last:border-b-0"
          >
            <div class="font-medium text-gray-700 min-w-[200px] tablet:min-w-[250px]">
              {{ getLocalizedText(field.label, currentLang) }}:
            </div>
            <div class="text-gray-600 flex-1 break-words">
              {{ formatValue(field, formValues[field.fieldName]) }}
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="fieldsBySection.length === 0" class="text-center text-gray-500 py-8">
        {{ getLocalizedText({ 
          fr: 'Aucun champ rempli à afficher.', 
          en: 'No filled fields to display.' 
        }, currentLang) }}
      </div>
    </div>
  </div>
</template>

